import { useEffect, useState } from "react";
import { ImageOff, ShoppingCart, Check, Beer, Droplets, Package, Tag } from "lucide-react";
import { useCart } from "../../context/CartContext";
import { Link } from "react-router-dom";
import { getProductUnits } from "../../services/productService";
import { formatMoney } from "../../utils/money";

// Purely a VISUAL mapping — the label rendered on the badge always comes
// straight from the real Backend API (category.name), never overridden
// here. This only decides which icon/color a recognized category gets.
//
// Matched against category.description as well as category.name: the
// real categories in this project's data are Khmer script
// (e.g. "ស្រាបៀរ"/"ទឺកសុទ្ធ"), which would never match an English
// "beer"/"water" keyword on their own — but CategoryOut.description
// already carries the real English label ("Beer"/"Water") for each,
// straight from the backend, so checking both fields (instead of name
// alone) is what actually makes this mapping work against real data
// rather than only ever hitting the neutral fallback.
const CATEGORY_STYLES = {
  beer: { Icon: Beer, bg: "bg-green-50", text: "text-green-700" },
  water: { Icon: Droplets, bg: "bg-blue-50", text: "text-blue-700" },
  other: { Icon: Package, bg: "bg-purple-50", text: "text-purple-700" },
};
const FALLBACK_CATEGORY_STYLE = { Icon: Tag, bg: "bg-neutral-100", text: "text-neutral-600" };

function getCategoryStyle(category) {
  const haystack = [category?.description, category?.name]
    .filter(Boolean)
    .join(" ")
    .trim()
    .toLowerCase();
  if (haystack.includes("beer")) return CATEGORY_STYLES.beer;
  if (haystack.includes("water")) return CATEGORY_STYLES.water;
  if (haystack.includes("other")) return CATEGORY_STYLES.other;
  return FALLBACK_CATEGORY_STYLE;
}

// Renders ONLY public-safe fields. Never render cost_price,
// income_account_id, cogs_account_id, asset_account_id, merged_into_id,
// or a ProductUnit row's own cost_price — the backend includes them in
// both GET /products and GET /products/{id}/units, but this card must
// never display them regardless of what the API response carries.
// Stock quantity itself is also never shown — only a derived
// In Stock / Out of Stock badge (the real number stays internal).
export default function ProductCard({ product, showPrice, prefetchedUnits }) {
  const { addItem } = useCart();
  const inStock = (product.stock ?? 0) > 0;

  // Real per-product units, from the existing GET /products/{id}/units
  // endpoint — never fabricated. A product with no configured units
  // (e.g. one never set up with Units & Pricing in frontend_Admin) falls
  // back to its own flat unit/price fields as a single implicit choice.
  const [units, setUnits] = useState([]);
  const [selectedUnit, setSelectedUnit] = useState(null); // a real ProductUnitOut row, or null (= use product.unit/product.price)

  useEffect(() => {
    let cancelled = false;
    // The shop grid passes units fetched in one batch request; only fall back
    // to a per-product request when they were not provided (other pages, or
    // the batch call failed).
    const source = prefetchedUnits !== undefined ? Promise.resolve(prefetchedUnits) : getProductUnits(product.id);
    source
      .then((rows) => {
        if (cancelled) return;
        // Only Active units are ever selectable here — the backend's own
        // order/quote path (crud.resolve_product_unit) silently ignores a
        // deactivated unit_code and re-prices the line off the product's
        // base price instead, so letting a customer pick a deactivated
        // unit would show one price in the cart and record a different
        // one server-side. Filtering here, not just at submission time,
        // is what actually prevents that mismatch from being shown at all.
        const active = rows.filter((u) => u.status === "Active");
        setUnits(active);
        // Default selection follows the existing backend data: prefer the
        // unit flagged is_base_unit (matches Product.price/currency, so
        // the price shown here stays consistent with the rest of the app
        // — Cart/Quote already priced everything off the base unit before
        // this feature existed); fall back to the first configured unit.
        const base = active.find((u) => u.is_base_unit) || active[0] || null;
        setSelectedUnit(base);
      })
      .catch(() => {
        if (!cancelled) {
          setUnits([]);
          setSelectedUnit(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [product.id, prefetchedUnits]);

  const price = selectedUnit ? selectedUnit.selling_price : product.price;
  const unitLabel = selectedUnit ? selectedUnit.unit_code : product.unit;

  const [feedback, setFeedback] = useState(false);
  const [imgFailed, setImgFailed] = useState(false);
  const showImage = Boolean(product.image) && !imgFailed;

  const categoryStyle = product.category?.name ? getCategoryStyle(product.category) : null;
  const CategoryIcon = categoryStyle?.Icon;

  const handleAddToCart = () => {
    if (!inStock) return;
    // Pass the real selected unit row (or null, when this product has no
    // configured units at all) — CartContext resolves unit_code/price
    // from it, falling back to the flat product fields when null.
    addItem(product, selectedUnit, 1);
    setFeedback(true);
    setTimeout(() => setFeedback(false), 1200);
  };

  return (
    <div className="rounded-2xl border border-neutral-200 bg-white overflow-hidden shadow-sm hover:shadow-md transition-shadow flex flex-col h-full">
      {/* Header — category badge + reserved favorite slot, fixed height so
          the image never starts at a different vertical position between
          cards that do/don't have a category. */}
      <div className="px-3 pt-3 pb-2 min-h-[36px] flex items-center justify-between gap-2">
        {categoryStyle ? (
          <span
            className={`inline-flex items-center gap-1 text-[11px] font-medium ${categoryStyle.text} ${categoryStyle.bg} rounded-full pl-1.5 pr-2.5 py-1 truncate max-w-[75%]`}
          >
            <CategoryIcon size={13} aria-hidden="true" />
            {product.category.name}
          </span>
        ) : (
          <span />
        )}
        {/* No favorite button: this app has no favorites feature/API today
            (confirmed — nothing to wire it to), so this slot is left empty
            rather than adding a heart icon that would do nothing when
            clicked. Reserved here only to keep the header row's height
            identical whether or not a card ever gets one later. */}
      </div>

      {/* Fixed-height image container — identical for every card
          regardless of the source image's own proportions/background. */}
      <div className="h-40 sm:h-48 md:h-52 bg-neutral-50 flex items-center justify-center px-5 py-3">
        {showImage ? (
          <img
            src={product.image}
            alt={product.name}
            onError={() => setImgFailed(true)}
            className="max-w-full max-h-full object-contain"
          />
        ) : (
          <div className="flex flex-col items-center gap-1.5 text-neutral-300">
            <ImageOff size={28} />
            <span className="text-[11px] text-neutral-400">Image unavailable</span>
          </div>
        )}
      </div>

      <div className="p-3 flex flex-col gap-1.5 flex-1">
        <h3 className="text-sm font-semibold text-neutral-900 line-clamp-2 min-h-[2.5em]">
          <Link to={`/product/${product.id}`} className="hover:underline">
            {product.name}
          </Link>
        </h3>

        <p className="text-xs text-neutral-500 line-clamp-2 min-h-[2em]">
          {product.description || ""}
        </p>

        {unitLabel && (
          <div className="mt-1">
            <p className="text-[11px] text-neutral-400 mb-1">Unit</p>
            <div className="flex flex-wrap gap-1.5">
              {(units.length > 0 ? units : [{ unit_code: unitLabel, selling_price: product.price }]).map((u) => (
                <button
                  key={u.unit_code}
                  type="button"
                  onClick={() => setSelectedUnit(units.length > 0 ? u : null)}
                  className={`text-xs rounded-full border px-2.5 py-1 transition-colors ${
                    (selectedUnit ? selectedUnit.unit_code : unitLabel) === u.unit_code
                      ? "border-neutral-900 bg-neutral-900 text-white"
                      : "border-neutral-300 text-neutral-600 hover:border-neutral-400"
                  }`}
                >
                  {u.unit_code}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="mt-auto pt-2 flex items-center justify-between">
          {showPrice ? (
            <span className="text-sm font-bold text-neutral-900">
              {formatMoney(price, product.currency)}
            </span>
          ) : (
            <span className="text-xs text-neutral-400">Price on request</span>
          )}
          <span
            className={`inline-flex items-center gap-1 text-xs rounded-full px-2 py-0.5 shrink-0 ${
              inStock ? "bg-green-50 text-green-700" : "bg-red-50 text-red-600"
            }`}
          >
            <span className={`w-1.5 h-1.5 rounded-full ${inStock ? "bg-green-500" : "bg-red-500"}`} />
            {inStock ? "In Stock" : "Out of Stock"}
          </span>
        </div>

        <button
          type="button"
          onClick={handleAddToCart}
          disabled={!inStock}
          className="mt-2 w-full inline-flex items-center justify-center gap-2 rounded-lg px-3 py-2.5 text-sm font-medium bg-neutral-900 text-white hover:bg-neutral-800 disabled:bg-neutral-100 disabled:text-neutral-400 disabled:cursor-not-allowed transition-colors"
        >
          {feedback ? <Check size={16} /> : <ShoppingCart size={16} />}
          {inStock ? (feedback ? "Added" : "Add to cart") : "Out of stock"}
        </button>
      </div>
    </div>
  );
}
